import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

type UseGetCreatorSearchFiltersDataResult = {
  savedFilters: string | null;
  filtersMetaFieldUuid: string | null;
};

/**
 * Custom hook to retrieve saved creator search filters and filters meta field UUID from project metadata.
 *
 * This hook uses selective Redux selection to prevent unnecessary re-renders
 * when unrelated projectBasicData fields are updated via websockets.
 *
 * Instead of selecting the entire projectBasicData object, it extracts only the
 * savedFilters value and filtersMetaFieldUuid, ensuring the component only re-renders
 * when these specific values actually change.
 *
 * @returns {UseGetCreatorSearchFiltersDataResult} An object containing:
 *   - `savedFilters`: The saved filters JSON string from project metadata, or null if not found
 *   - `filtersMetaFieldUuid`: The UUID of the filters meta field, or null if not found
 *
 * @example
 * ```tsx
 * const { savedFilters, filtersMetaFieldUuid } = useGetCreatorSearchFiltersData();
 *
 * useEffect(() => {
 *   if (savedFilters) {
 *     const parsedFilters = JSON.parse(savedFilters);
 *     // Use parsed filters...
 *   }
 * }, [savedFilters]);
 * ```
 */
export default function useGetCreatorSearchFiltersData(): UseGetCreatorSearchFiltersDataResult {
  return useSelector(
    (state: RootState): UseGetCreatorSearchFiltersDataResult => {
      const projectBasicData = state.projectReducer.projectBasicData;
      const filtersMetaFieldUuid =
        projectBasicData?.filtersMetaFieldUuid ?? null;
      const filtersObject = projectBasicData?.metadata?.find(
        (item) => item.uuid === filtersMetaFieldUuid,
      );

      const savedFilters =
        filtersObject &&
        (typeof filtersObject.value === "string" ||
          filtersObject.value === null)
          ? (filtersObject.value as string | null)
          : null;

      return { savedFilters, filtersMetaFieldUuid };
    },
  );
}
